import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Check, CheckCircle2, ChevronDown, ExternalLink, Loader2, Lock, ShieldCheck } from "lucide-react";
import { track } from "@/lib/analytics";
import { checkPaypalOrder, createPaypalOrder, paypalErrorMessage, reportPaypalEvent } from "@/lib/paypalCheckout";

/**
 * PayPal checkout for buyers outside India (India pays through Razorpay).
 *
 * What the buyer sees: an order summary, one gold PayPal button, then a waiting screen that turns into the
 * success screen by itself. Behind it the server holds the PayPal links and credits the purchase the moment PayPal
 * confirms it, so the buyer never has to report back. If they paid with a different PayPal e-mail than their
 * account's, a transaction-id box lets them claim the payment in one step.
 *
 * Props
 *   item   { kind: "pack"|"pass", key, name, sub?, price, perks: string[], badge?, mode: "link"|"api" }
 *   onBack ()        go back to the list of packs / passes
 *   onPaid (result)  the server's answer once it has credited: {state:"paid", kind, balance?, tokens_credited?, pass_until?, amount_label}
 *
 * "api" mode sends the buyer to PayPal in this tab and brings them back to /wallet (PayPalReturnHandler finishes it);
 * "link" mode opens PayPal in a new tab and watches this one.
 */
const MAX_WAIT_S = 20 * 60;
const CLAIM_HINT_AFTER_S = 25;

function PayPalWord() {
  return (
    <span className="font-black italic tracking-tight text-[17px] leading-none">
      <span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span>
    </span>
  );
}

const STEPS = [
  ["Pay on PayPal", "A secure PayPal page opens"],
  ["We confirm it", "Automatically, in seconds"],
  ["Ready to use", "Credited to your account"],
];

export default function PayPalCheckout({ item, source = "dialog", onBack, onPaid }) {
  const [step, setStep] = useState("review");        // review | starting | waiting | timeout | problem | paid
  const [order, setOrder] = useState(null);
  const [message, setMessage] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [txn, setTxn] = useState("");
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimNote, setClaimNote] = useState("");
  const alive = useRef(true);
  const done = useRef(false);
  const timer = useRef(null);
  const started = useRef(0);
  const ref = useRef(null);
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;                    // always the parent's latest, even inside a timer started earlier
  const price = Number(item.price);
  const props = { item: item.key, kind: item.kind, price, currency: "USD", source, mode: item.mode };

  const finish = useCallback((res) => {
    if (done.current) return;
    done.current = true;
    clearTimeout(timer.current);
    setStep("paid");
    track("paypal_paid", props);
    onPaidRef.current?.(res);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const poll = useCallback(async () => {
    clearTimeout(timer.current);
    if (!alive.current || done.current || !ref.current) return;
    const elapsed = (Date.now() - started.current) / 1000;
    setSeconds(Math.floor(elapsed));
    if (elapsed > MAX_WAIT_S) {
      setStep("timeout");
      track("paypal_wait_timeout", props);
      reportPaypalEvent("wait_timeout", { orderRef: ref.current, itemKey: item.key });
      return;
    }
    try {
      const res = await checkPaypalOrder(ref.current);
      if (!alive.current || done.current) return;
      if (res.state === "paid") { finish(res); return; }
      if (res.state === "problem" || res.state === "failed") {
        setStep("problem");
        setMessage(res.message || (res.state === "failed" ? "PayPal didn't complete this payment. You were not charged." : "We couldn't match this payment to your order."));
        track("paypal_failed", { ...props, reason: res.state });
        return;
      }
    } catch { /* a blip on our side or theirs: the next check will do */ }
    timer.current = setTimeout(poll, elapsed < 60 ? 3000 : 6000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finish]);

  useEffect(() => {
    alive.current = true;
    track("paypal_checkout_viewed", props);
    const wake = () => { if (document.visibilityState !== "hidden" && ref.current && !done.current) poll(); };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pay = async () => {
    if (step === "starting") return;
    setMessage("");
    setStep("starting");
    track("paypal_checkout_started", props);
    const sameTab = item.mode === "api";
    let win = null;
    if (!sameTab) {
      // open the tab now, inside the tap, so the browser allows it; point it at PayPal once the server has the order
      try {
        win = window.open("", "_blank");
        if (win) {
          win.opener = null;
          win.document.title = "PayPal";
          win.document.body.style.cssText = "margin:0;display:flex;align-items:center;justify-content:center;height:100vh;font:16px system-ui;color:#555";
          win.document.body.textContent = "Opening PayPal securely…";
        }
      } catch { win = null; }
    }
    let data;
    try {
      data = await createPaypalOrder(item.kind, item.key);
    } catch (e) {
      try { win?.close(); } catch { /* already gone */ }
      const msg = paypalErrorMessage(e, "We couldn't start the payment. Please try again in a moment.");
      setStep("problem");
      setMessage(msg);
      track("paypal_failed", { ...props, reason: "create" });
      reportPaypalEvent("create_failed", { itemKey: item.key, reason: msg });
      return;
    }
    ref.current = data.order_ref;
    setOrder(data);
    if (sameTab) {
      track("paypal_redirected", props);
      window.location.assign(data.url);
      return;
    }
    let opened = false;
    try {
      if (win && !win.closed) { win.location.href = data.url; opened = true; }
    } catch { opened = false; }
    setPopupBlocked(!opened);
    track(opened ? "paypal_opened" : "paypal_popup_blocked", props);
    reportPaypalEvent(opened ? "opened" : "popup_blocked", { orderRef: data.order_ref, itemKey: item.key });
    done.current = false;
    started.current = Date.now();
    setSeconds(0);
    setStep("waiting");
    timer.current = setTimeout(poll, 2500);
  };

  const goBack = () => {
    if (step === "waiting" || step === "timeout") {
      track("paypal_checkout_closed", { ...props, after_s: seconds });
      reportPaypalEvent("closed_unpaid", { orderRef: ref.current, itemKey: item.key });
    }
    onBack?.();
  };

  const keepWaiting = () => {
    started.current = Date.now();
    setSeconds(0);
    setStep("waiting");
    timer.current = setTimeout(poll, 1000);
  };

  const submitClaim = async (e) => {
    e.preventDefault();
    const t = txn.trim();
    if (t.length < 8) { setClaimNote("Paste the transaction ID from PayPal's confirmation email (about 17 letters and numbers)."); return; }
    setClaimBusy(true);
    setClaimNote("");
    track("paypal_claim_submitted", props);
    try {
      const res = await checkPaypalOrder(ref.current, t);
      if (res.state === "paid") { finish(res); return; }
      if (res.state === "problem") {
        setClaimNote(res.message || "That transaction doesn't match this order.");
        reportPaypalEvent("claim_failed", { orderRef: ref.current, itemKey: item.key, reason: res.message });
      } else {
        setClaimNote("Thanks, we have your transaction ID. PayPal hasn't confirmed it to us yet; your purchase is credited automatically the moment it does.");
      }
    } catch (err) {
      const m = paypalErrorMessage(err);
      setClaimNote(m);
      reportPaypalEvent("claim_failed", { orderRef: ref.current, itemKey: item.key, reason: m });
    } finally {
      setClaimBusy(false);
    }
  };

  const total = `$${price}`;
  const claimForm = (
    <div className="text-left">
      <button type="button" onClick={() => setClaimOpen((v) => !v)} aria-expanded={claimOpen}
        className="mx-auto flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-200 underline underline-offset-4">
        Paid with a different PayPal email? <ChevronDown className={`w-3.5 h-3.5 transition-transform ${claimOpen ? "rotate-180" : ""}`} />
      </button>
      {claimOpen && (
        <form onSubmit={submitClaim} className="mt-3 rounded-xl border border-zinc-700 bg-zinc-900/70 p-3 space-y-2">
          <label htmlFor="pp-txn" className="block text-[12px] text-zinc-300">
            Enter the <strong className="text-white">transaction ID</strong> from PayPal's receipt email (or Activity) and we'll find your payment.
          </label>
          <div className="flex gap-2">
            <input id="pp-txn" value={txn} onChange={(e) => setTxn(e.target.value)} placeholder="e.g. 3C679366HH908993F" autoComplete="off" spellCheck={false}
              className="min-w-0 flex-1 rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-lime-400/60" />
            <button type="submit" disabled={claimBusy}
              className="shrink-0 rounded-lg bg-zinc-100 text-black text-sm font-semibold px-4 disabled:opacity-60">
              {claimBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find it"}
            </button>
          </div>
          {claimNote && <p role="status" className="text-[12px] text-amber-300 leading-snug">{claimNote}</p>}
        </form>
      )}
    </div>
  );

  // ─────────── the screens ───────────
  if (step === "paid") {
    return (
      <div className="py-8 text-center" role="status" data-testid="paypal-paid">
        <CheckCircle2 className="w-12 h-12 text-lime-400 mx-auto" />
        <h3 className="text-white text-lg font-bold mt-3">Payment received</h3>
        <p className="text-sm text-zinc-400 mt-1">Thank you. Your purchase is ready.</p>
      </div>
    );
  }

  if (step === "problem") {
    return (
      <div className="space-y-4" data-testid="paypal-problem">
        <div role="alert" className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0 text-amber-300 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">We couldn't complete that</p>
            <p className="text-[13px] text-zinc-300 mt-1 leading-snug">{message || "Something went wrong."}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => { setStep("review"); setMessage(""); }}
            className="flex-1 rounded-full bg-zinc-100 text-black font-semibold py-2.5 text-sm">Try again</button>
          <a href="mailto:info@formanti.com?subject=Payment%20help" className="flex-1 text-center rounded-full border border-zinc-700 text-zinc-200 font-semibold py-2.5 text-sm">Contact support</a>
        </div>
        <button type="button" onClick={goBack} className="mx-auto flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-300"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
      </div>
    );
  }

  if (step === "waiting" || step === "timeout") {
    const timedOut = step === "timeout";
    return (
      <div className="space-y-4 text-center" data-testid="paypal-waiting">
        <div className="relative mx-auto w-16 h-16 mt-1">
          {!timedOut && <span className="absolute inset-0 rounded-full bg-lime-400/20 animate-ping" />}
          <span className="relative flex items-center justify-center w-16 h-16 rounded-full bg-zinc-800 border border-lime-400/40">
            {timedOut ? <AlertTriangle className="w-6 h-6 text-amber-300" /> : <Lock className="w-6 h-6 text-lime-300" />}
          </span>
        </div>
        <div role="status" aria-live="polite">
          <h3 className="text-white text-lg font-bold">{timedOut ? "Still waiting for PayPal" : "Waiting for PayPal"}</h3>
          <p className="text-[13px] text-zinc-400 mt-1 leading-snug max-w-sm mx-auto">
            {timedOut
              ? "We haven't had PayPal's confirmation yet. If you've paid, please don't pay again: it's credited automatically as soon as PayPal confirms, usually within minutes."
              : "Finish paying in the PayPal window. This page updates by itself the moment your payment is confirmed."}
          </p>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3 flex items-center justify-between text-left">
          <span className="text-sm text-zinc-200 min-w-0 truncate">{item.name}</span>
          <span className="text-sm font-bold text-white shrink-0 ml-3">{total} <span className="text-zinc-500 font-normal text-[11px]">USD</span></span>
        </div>
        {order?.url && (popupBlocked || timedOut) && (
          <a href={order.url} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-[#FFC439] hover:bg-[#f2b92f] text-[#111] font-semibold">
            Open <PayPalWord /> to pay <ExternalLink className="w-4 h-4" />
          </a>
        )}
        {timedOut ? (
          <button type="button" onClick={keepWaiting} className="mx-auto block rounded-full border border-zinc-700 text-zinc-200 text-sm font-semibold px-5 py-2">Keep waiting</button>
        ) : (
          !popupBlocked && order?.url && (
            <a href={order.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-200 underline underline-offset-4">
              PayPal window didn't open? Open it again <ExternalLink className="w-3 h-3" />
            </a>
          )
        )}
        {(seconds >= CLAIM_HINT_AFTER_S || timedOut || claimOpen) && claimForm}
        <p className="text-[11px] text-zinc-500 leading-snug">Already paid? You can close this. Your purchase is credited automatically, even if you leave.</p>
        <button type="button" onClick={goBack} className="mx-auto flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-300"><ArrowLeft className="w-3.5 h-3.5" /> Back to options</button>
      </div>
    );
  }

  // review (and "starting", which keeps the same layout with the button busy)
  const busy = step === "starting";
  return (
    <div className="space-y-4" data-testid="paypal-review">
      <button type="button" onClick={goBack} className="flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-200"><ArrowLeft className="w-3.5 h-3.5" /> All options</button>

      <div className="rounded-2xl border border-zinc-700/70 bg-gradient-to-br from-zinc-800/70 to-zinc-900 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider font-bold text-lime-300">{item.badge || "Your order"}</p>
            <h3 className="text-lg sm:text-xl font-bold text-white mt-0.5 leading-tight">{item.name}</h3>
            {item.sub && <p className="text-xs text-zinc-400 mt-1">{item.sub}</p>}
          </div>
          <div className="text-right shrink-0">
            <p className="font-heading font-black text-3xl text-white leading-none">{total}</p>
            <p className="text-[10px] text-zinc-500 mt-1.5">USD · one-time payment</p>
          </div>
        </div>
        {item.perks?.length > 0 && (
          <ul className="mt-4 space-y-1.5 border-t border-zinc-700/60 pt-3">
            {item.perks.map((t) => (
              <li key={t} className="flex gap-2 text-[13px] text-zinc-200 leading-snug"><Check className="w-4 h-4 shrink-0 mt-0.5 text-lime-400" strokeWidth={3} />{t}</li>
            ))}
          </ul>
        )}
      </div>

      <ol className="grid grid-cols-3 gap-2" aria-label="How it works">
        {STEPS.map(([title, text], i) => (
          <li key={title} className="rounded-xl bg-zinc-900/70 border border-zinc-800 px-2.5 py-2.5">
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-zinc-800 text-[10px] font-bold text-lime-300">{i + 1}</span>
            <p className="text-[12px] font-semibold text-zinc-100 mt-1.5 leading-tight">{title}</p>
            <p className="text-[10.5px] text-zinc-500 mt-0.5 leading-snug">{text}</p>
          </li>
        ))}
      </ol>

      <button type="button" onClick={pay} disabled={busy} data-testid="paypal-pay" data-cta="paypal_pay"
        className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-[#FFC439] hover:bg-[#f2b92f] text-[#111] font-semibold shadow-lg shadow-amber-500/10 transition active:scale-[0.99] disabled:opacity-80">
        {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Opening PayPal…</> : <>Pay {total} with <PayPalWord /></>}
      </button>

      <div className="space-y-1.5">
        <p className="flex items-center justify-center gap-1.5 text-[11.5px] text-zinc-400"><Lock className="w-3.5 h-3.5 text-lime-400" /> Secure checkout by PayPal. Your payment details go to PayPal, never to Formanti.</p>
        <p className="flex items-center justify-center gap-1.5 text-[11px] text-zinc-500 text-center"><ShieldCheck className="w-3.5 h-3.5 shrink-0" /> <span>Digital service, delivered as soon as PayPal confirms. See the <Link to="/refund" className="underline">refund policy</Link>.</span></p>
      </div>
    </div>
  );
}
